import { FE_ENV, isTruthyEnvVar } from '../../frontend-env';

/**
 * Client-side logger that sends logs to server
 * Supports different log levels and structured context data
 * Includes throttling and batching to prevent backend overflow
 */
class ClientLogger {
  constructor() {
    this.isLoggingEnabled =
      FE_ENV.NODE_ENV === 'development' ||
      window.location.hostname.includes('stageapp') ||
      isTruthyEnvVar(FE_ENV.REACT_APP_LOGGING_ENABLED);
    this.logQueue = [];
    this.isOnline = navigator.onLine;
    this.lastLogTimes = new Map(); // For throttling
    this.logCounts = new Map(); // For rate limiting
    this.batchTimer = null;
    this.maxQueueSize = 50; // Prevent memory overflow
    this.throttleIntervals = {
      debug: 5000, // Debug logs: max 1 per 5 seconds
      info: 2000, // Info logs: max 1 per 2 seconds
      warn: 1000, // Warn logs: max 1 per second
      error: 500, // Error logs: max 1 per 500ms
    };
    this.maxLogsPerMinute = 30; // Global rate limit

    // Listen for online/offline events
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.flushQueue();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
    });
  }

  shouldThrottle(logLevel, message) {
    if (!this.isLoggingEnabled) return true;

    // Check global rate limit
    const now = Date.now();
    const minuteAgo = now - 60000;

    // Clean old entries
    this.logCounts.forEach((time, key) => {
      if (time < minuteAgo) {
        this.logCounts.delete(key);
      }
    });

    // Check if we've exceeded global limit
    if (this.logCounts.size >= this.maxLogsPerMinute) {
      return true;
    }

    // Check throttle for this specific message type
    const throttleKey = `${logLevel}:${message}`;
    const lastTime = this.lastLogTimes.get(throttleKey);
    const throttleInterval = this.throttleIntervals[logLevel] || 1000;

    if (lastTime && now - lastTime < throttleInterval) {
      return true; // Throttled
    }

    // Update tracking
    this.lastLogTimes.set(throttleKey, now);
    this.logCounts.set(`${now}_${this.getSecureRandomString(6)}`, now);

    return false;
  }

  async sendToServer(logLevel, message, context = {}) {
    if (this.shouldThrottle(logLevel, message)) {
      return; // Skip throttled logs
    }

    // Prevent queue overflow
    if (this.logQueue.length >= this.maxQueueSize) {
      this.logQueue.shift(); // Remove oldest log
    }

    const logData = {
      logLevel,
      message,
      context: {
        ...context,
        pathname: window.location.pathname,
        search: window.location.search,
        timestamp: new Date().toISOString(),
        sessionId: this.getSessionId(),
      },
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: new Date().toISOString(),
    };

    if (!this.isOnline) {
      this.logQueue.push(logData);
      return;
    }

    // Add to queue for batching
    this.logQueue.push(logData);
    this.scheduleBatch();
  }

  scheduleBatch() {
    if (this.batchTimer) return; // Already scheduled

    this.batchTimer = setTimeout(() => {
      this.flushQueue();
      this.batchTimer = null;
    }, 1000); // Batch logs every 1 second
  }

  async flushQueue() {
    if (this.logQueue.length === 0 || !this.isOnline) return;

    const logsToSend = [...this.logQueue];
    this.logQueue = [];

    // Send logs in batch if multiple, or individually if few
    if (logsToSend.length > 5) {
      const ac = new AbortController();
      const timer = setTimeout(() => ac.abort(), 10000);
      try {
        // Send as batch to reduce HTTP requests
        const resp = await fetch('/api/v1/general/ClientLogBatch', {
          method: 'POST',
          credentials: 'include',
          signal: ac.signal,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ logs: logsToSend }),
        });
        if (!resp.ok) throw new Error(`ClientLogBatch ${resp.status}`);
      } catch (error) {
        // Fall back to individual sends or re-queue a subset
        const criticalLogs = logsToSend.filter((log) => log.logLevel === 'error' || log.logLevel === 'warn');
        this.logQueue.push(...criticalLogs.slice(0, 10)); // Keep only critical logs
      } finally {
        clearTimeout(timer);
      }
    } else {
      // Send individual logs for small batches
      for (const logData of logsToSend) {
        const ac = new AbortController();
        const timer = setTimeout(() => ac.abort(), 5000);
        try {
          const resp = await fetch('/api/v1/general/ClientLog', {
            method: 'POST',
            credentials: 'include',
            signal: ac.signal,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(logData),
          });
          if (!resp.ok) throw new Error(`ClientLog ${resp.status}`);
        } catch (error) {
          // Only re-queue critical logs to prevent overflow
          if (logData.logLevel === 'error' || logData.logLevel === 'warn') {
            this.logQueue.push(logData);
          }
          break; // Stop sending if one fails
        } finally {
          clearTimeout(timer);
        }
      }
    }
  }

  /**
   * Generates a cryptographically secure random string
   * @param {number} length - Length of the random string
   * @returns {string} Secure random string
   */
  getSecureRandomString(length = 9) {
    // Use crypto.getRandomValues for cryptographically secure randomness
    // crypto is available in modern browsers via Web Crypto API
    // Generate enough bytes to ensure we have sufficient entropy
    // Each byte (0-255) converted to base36 gives 1-2 chars, so we generate 'length' bytes
    const array = new Uint8Array(length);
    window.crypto.getRandomValues(array);
    // Convert to base36 string (0-9, a-z) and take exactly 'length' characters
    // Each byte produces at least 1 base36 character, so we'll have at least 'length' chars
    return Array.from(array, (byte) => byte.toString(36))
      .join('')
      .substring(0, length);
  }

  getSessionId() {
    let sessionId = sessionStorage.getItem('clientLogSessionId');
    if (!sessionId) {
      sessionId = 'sess_' + Date.now() + '_' + this.getSecureRandomString(9);
      sessionStorage.setItem('clientLogSessionId', sessionId);
    }
    return sessionId;
  }

  // Public logging methods - with reduced volume
  info(message, context = {}) {
    // Only log critical info events
    if (this.isCriticalInfo(message)) {
      // eslint-disable-next-line no-console
      console.info(`[CLIENT-LOG] ${message}`, context);
      this.sendToServer('info', message, context);
    }
  }

  warn(message, context = {}) {
    // eslint-disable-next-line no-console
    console.warn(`[CLIENT-LOG] ${message}`, context);
    this.sendToServer('warn', message, context);
  }

  error(message, context = {}) {
    // eslint-disable-next-line no-console
    console.error(`[CLIENT-LOG] ${message}`, context);
    this.sendToServer('error', message, context);
  }

  debug(message, context = {}) {
    // Only log debug in development
    if (FE_ENV.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.debug(`[CLIENT-LOG] ${message}`, context);
      this.sendToServer('debug', message, context);
    }
  }

  isCriticalInfo(message) {
    const criticalPatterns = ['Login', 'Authentication', 'Error', 'Failed', 'Success', 'Performance', 'API call'];
    return criticalPatterns.some((pattern) => message.includes(pattern));
  }

  // Login-specific logging methods
  loginAttempt(loginType, userEmail = null) {
    this.info('Login attempt started', {
      loginType,
      userEmail: userEmail ? userEmail.replace(/(.{2})(.*)(@.*)/, '$1***$3') : null, // Mask email
      hasToken: !!localStorage.getItem('token'),
      timestamp: new Date().toISOString(),
    });
  }

  loginSuccess(userEmail = null) {
    this.info('Login successful', {
      userEmail: userEmail ? userEmail.replace(/(.{2})(.*)(@.*)/, '$1***$3') : null,
      hasToken: !!localStorage.getItem('token'),
      timestamp: new Date().toISOString(),
    });
  }

  loginFailure(error, context = {}) {
    // Sanitize error to prevent logging sensitive information
    const sanitizedError = this.sanitizeError(error);

    this.error('Login failed', {
      error: sanitizedError.message,
      errorType: sanitizedError.type,
      ...context,
      hasToken: !!localStorage.getItem('token'),
      timestamp: new Date().toISOString(),
    });
  }

  // Sanitize errors to prevent logging sensitive data
  sanitizeError(error) {
    if (!error) return { message: 'Unknown error', type: 'UnknownError' };

    let message = 'Unknown error';
    let type = 'UnknownError';

    if (error.message) {
      // Remove potential sensitive data from error messages
      message = error.message
        .replace(/password|token|key|secret/gi, '[REDACTED]')
        .replace(/\b[\w.-]+@[\w.-]+\.\w+\b/g, '[EMAIL]') // Remove emails
        .replace(/\b(?:\d{4}[\s-]?){3}\d{4}\b/g, '[CARD]') // Remove card numbers
        .substring(0, 200); // Limit length
    }

    if (error.name) {
      type = error.name;
    }

    return { message, type };
  }

  apiCall(endpoint, method = 'GET', status = null, duration = null) {
    const level = status >= 400 ? 'error' : status >= 300 ? 'warn' : 'info';
    this[level]('API call', {
      endpoint,
      method,
      status,
      duration,
      hasToken: !!localStorage.getItem('token'),
      timestamp: new Date().toISOString(),
    });
  }

  performance(action, duration, context = {}) {
    this.info('Performance metric', {
      action,
      duration,
      ...context,
      timestamp: new Date().toISOString(),
    });
  }
}

// Create singleton instance
const clientLogger = new ClientLogger();

export default clientLogger;
